<template>
  <v-container fluid class="fill-height">
    <v-row no-gutters align="stretch">
      <!-- Sidebar: Threads -->
      <v-col cols="12" md="3" class="pa-2">
        <v-card outlined rounded elevation="2" class="fill-height">
          <v-card-title class="text-h6 primary white--text pa-3 d-flex align-center">
            🧵 Your Threads
            <v-menu
                bottom
                offset-y
                close-on-content-click
                open-on-hover
            >
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                    v-bind="attrs"
                    v-on="on"
                    color="white"
                    small
                    class="ml-2"
                    style="cursor: help;"
                >
                  mdi-help-circle
                </v-icon>
              </template>
              <v-card
                  flat
                  class="pa-3"
                  style="white-space: normal; max-width: 320px; line-height: 1.4; font-size: 13px; user-select: text;"
              >
                <strong>This assistant uses RAG</strong> (Retrieval-Augmented Generation) to answer data-related questions.<br /><br />
                Ask about <em>conversions</em>, <em>traffic</em>, <em>affiliate performance</em>, <em>time periods</em>, etc.<br /><br />
                Examples:<br />

                <div
                    v-for="(example, index) in examples"
                    :key="index"
                    class="d-flex align-center mb-2"
                    style="gap: 8px"
                >
                  <div style="flex: 1;">• {{ example }}</div>
                  <v-btn icon small @click="copy(example)">
                    <v-icon size="16">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
              </v-card>

            </v-menu>
          </v-card-title>
          <v-divider />
          <v-btn
              block
              color="success"
              dark
              class="mt-2"
              @click="createThread"
              :loading="creatingThread"
          >
            + New Thread
          </v-btn>
          <v-list dense class="overflow-y-auto" style="max-height: calc(100vh - 200px)">
            <v-list-item
                v-for="thread in threads"
                :key="thread.thread_id"
                @click="selectThread(thread.thread_id)"
                :class="{ 'grey lighten-3': thread.thread_id === selectedThreadId }"
                :ripple="false"
                class="transition-fast-in-fast-out"
            >
              <v-list-item-content>
                <v-list-item-title>
                  {{ thread.title || `ID ${thread.thread_id}` }}<br />
                  <small class="grey--text">{{ formatDate(thread.created_at) }}</small>
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn icon small @click.stop="deleteThread(thread.thread_id)">
                  <v-icon color="primary">mdi-delete</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>

      <!-- Main: Chat + Ask -->
      <v-col cols="12" md="9" class="pa-2">
        <v-card outlined rounded elevation="2" class="d-flex flex-column" style="height: calc(100vh - 32px)">
          <v-card-title class="text-h6 primary white--text pa-3 d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              💬 Chat
            </div>
            <div class="d-flex align-center">
              <span class="mr-2" style="font-size: 14px;">
                {{ showSql ? 'Hide SQL' : 'Show SQL' }}
              </span>
              <v-switch
                  v-model="showSql"
                  inset
                  dense
                  hide-details
                  color="grey lighten-3"
              />
            </div>
          </v-card-title>
          <v-divider />
          <v-sheet class="overflow-y-auto pa-4" ref="messageBox" style="flex: 1 1 auto;">
            <v-row
                v-for="(msg, index) in filteredMessages"
                :key="index"
                class="mb-6"
                align="start"
                :ref="index === filteredMessages.length - 1 ? 'lastMessage' : null"
            >
              <v-col cols="1" class="d-flex justify-center">
                <v-avatar
                    :color="msg.role === 'human' ? 'blue' : msg.role === 'sql' ? 'grey' : 'green'"
                    size="30"
                    class="white--text"
                >
                  {{ msg.role.charAt(0).toUpperCase() }}
                </v-avatar>
              </v-col>
              <v-col cols="11">
                <v-card
                    :class="{
                      'blue lighten-5': msg.role === 'human',
                      'green lighten-5': msg.role === 'ai',
                      'grey lighten-4': msg.role === 'sql'
                    }"
                    flat
                    class="pa-3"
                >
                  <div v-if="msg.role === 'sql'" class="sql-block">
                    <pre><code>{{ msg.content }}</code></pre>
                  </div>
                  <div v-else class="text-body-1">
                    <span v-if="msg.isThinking" class="thinking-dots">
                      {{ msg.content }}<span class="dot">.</span><span class="dot">.</span><span class="dot">.</span>
                    </span>
                    <span v-else>
                      {{ msg.content }}
                    </span>
                  </div>
                  <small class="grey--text ml-2">{{ msg.createdAt }}</small>
                </v-card>
              </v-col>
            </v-row>
          </v-sheet>
          <v-card-actions class="pa-4 sticky-input">
            <v-text-field
                v-model="question"
                label="Ask your question..."
                outlined
                dense
                clearable
                prepend-inner-icon="mdi-magnify"
                class="mt-0"
                :disabled="loading"
                @keyup.enter="submitQuestion"
            />
            <v-btn
                color="primary"
                :loading="loading"
                @click="submitQuestion"
                class="ml-2"
                :disabled="loading"
                elevation="2"
            >
              Submit
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import ragApi from '@/api/ragApi'
import { mapState } from "vuex";
import { devs, sales } from "@/constants";
import affiliates from "@/api/affiliates";

export default {
  computed: {
    ...mapState('user', ['user']),
    filteredMessages() {
      const msgs = this.showSql ? this.messages : this.messages.filter(msg => msg.role !== 'sql');
      const cleaned = msgs.filter(msg => !(msg.role === 'human' && msg.content.toLowerCase() === 'start'));

      if (
          cleaned.length === 1 &&
          cleaned[0].role === 'ai' &&
          cleaned[0].content.toLowerCase().includes('how can i help')
      ) {
        return cleaned;
      }

      if (
          cleaned.length >= 2 &&
          cleaned[0].role === 'ai' &&
          cleaned[0].content.toLowerCase().includes('how can i help')
      ) {
        return cleaned.slice(1);
      }

      return cleaned;
    },
  },
  data() {
    return {
      examples: [
        "How many sales did we have in July 2025?",
        "What was the total revenue last week?",
        "Top 5 affiliates by profit in June?",
        "Conversion rate by country for Q2 2025?",
        "Traffic breakdown by device type yesterday",
        "When was the first conversion for affiliate 2389?",
        "How many unique visitors did we get last month?",
        "What was the average payout per conversion in April 2025?",
        "Top 10 offers by total clicks this year",
        "Conversion trend by day over the past two weeks",
        "Which geo generated the most profit in Q1 2025?",
        "Top 5 affiliates with the highest ROI in July 2025",
        "Top 5 countries by number of conversions this month",
        "Top performing devices by revenue last quarter",
        "Conversion trend by day over the past two weeks?",
        // "Revenue trend by month this year?",
        "Which offer has the fastest growing traffic this quarter?",
      ],
      userEmail: '',
      threads: [],
      selectedThreadId: null,
      messages: [],
      question: '',
      loading: false,
      creatingThread: false,
      showSql: false
    }
  },
  async mounted() {
    const platform = await affiliates.getPlatform();
    this.userEmail = this.user[0].email
    const allowedUsers = [...devs, ...sales];
    if (!allowedUsers.includes(this.user[0].name) || platform === 1) {
      await this.$router.push('/403');
      return;
    }
    await this.fetchThreads()
  },
  methods: {
    copy(text) {
      navigator.clipboard.writeText(text);
      this.$dialog('Copied!'); // Optional: use toast if available
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleString()
    },
    async fetchThreads() {
      const res = await ragApi.getThreads(this.userEmail)
      this.threads = await Promise.all(res.map(async (thread) => ({
        ...thread,
        title: await this.generateThreadTitle(thread)
      })))
      if (res.length && !this.selectedThreadId) {
        await this.selectThread(res[0].thread_id)
      }
    },
    async selectThread(threadId) {
      this.selectedThreadId = threadId;
      const msgs = await ragApi.getThreadMessages(threadId, this.userEmail);
      this.messages = msgs;
      console.log(`[RAG_MESSAGES] `, this.messages);

      await this.$nextTick();
      this.scrollToBottom();

      setTimeout(() => {
        this.scrollToBottom();
      }, 100);
    },

    async submitQuestion() {
      if (!this.question) return;

      this.loading = true;

      try {
        this.messages = this.messages.filter(
            msg => !(msg.role === 'ai' && msg.content.toLowerCase().includes('how can i help'))
        );

        if (!this.selectedThreadId) {
          const newThreadRes = await ragApi.getAnswerFromDuckdbRag({
            threadId: null,
            userEmail: this.userEmail,
            question: 'Start'
          });

          if (newThreadRes?.threadId) {
            this.selectedThreadId = newThreadRes.threadId;
            await this.fetchThreads();
            await this.selectThread(newThreadRes.threadId);
            this.messages = [];
          } else {
            await this.$dialog.error({
              text: `Could not create a new thread.`,
              title: 'Validation Error'
            });
            return;
          }
        }

        const placeholder = {
          role: 'ai',
          content: 'Thinking',
          createdAt: new Date().toLocaleString(),
          isThinking: true
        };
        this.messages.push(placeholder);
        this.$nextTick(() => this.scrollToBottom());

        const res = await ragApi.getAnswerFromDuckdbRag({
          threadId: this.selectedThreadId,
          userEmail: this.userEmail,
          question: this.question
        });

        if (!res) {
          this.$dialog('No response received from the assistant.');
          this.messages = this.messages.filter(msg => !msg.isThinking);
          return;
        }

        if (res.metadata?.success) {
          this.selectedThreadId = res.threadId;
          console.log(`[RAG_ANSWER_SQL]`, res.sql);
          this.question = '';
          const idx = this.messages.findIndex(msg => msg.isThinking);
          if (idx !== -1) {
            this.$set(this.messages, idx, {
              role: 'ai',
              content: res.answer || '[No answer]',
              createdAt: new Date().toLocaleString()
            });
            this.$nextTick(() => this.scrollToBottom());
          }
          await this.fetchThreads();
          await this.selectThread(res.threadId);
        } else {
          const errorMessage = res.metadata?.error || 'Query failed';
          await this.$dialog.error({
            text: `Error: ${errorMessage}`,
            title: 'Error'
          });
          this.messages = this.messages.filter(msg => !msg.isThinking);
        }
      } catch (e) {
        console.error('submitQuestion error:', e);
        await this.$dialog.error({
          text: `submitQuestion An unexpected error occurred.`,
          title: 'submitQuestion Error'
        });
        this.messages = this.messages.filter(msg => !msg.isThinking);
      } finally {
        this.loading = false;
        this.$nextTick(() => this.scrollToBottom());
      }
    },

    async createThread() {
      this.creatingThread = true
      try {
        const res = await ragApi.getAnswerFromDuckdbRag({
          threadId: null,
          userEmail: this.userEmail,
          question: 'Start'
        })
        this.selectedThreadId = res.threadId
        await this.fetchThreads()
        await this.selectThread(res.threadId)
        this.messages = this.messages.slice(1)

      } catch (e) {
        console.error('Error creating thread:', e)
      } finally {
        this.creatingThread = false
      }
    },
    async deleteThread(threadId) {
      let isConfirmDelete = await this.$dialog.warning({
        text: `Are you sure you want to delete this thread and its messages?`,
        title: 'Delete Thread',
      })
      if (isConfirmDelete){
        try {
          await ragApi.deleteThread(threadId)
          if (this.selectedThreadId === threadId) {
            this.selectedThreadId = null
            this.messages = []
          }
          await this.fetchThreads()
        } catch (e) {
          console.error('Error deleting thread:', e)
        }
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const last = this.$refs.lastMessage;
        if (last && last[0]) {
          last[0].scrollIntoView({ behavior: 'auto', block: 'end' }); // or 'smooth'
        }
      });
    },
    generateThreadTitle(thread) {
      return `Thread ID-${thread.thread_id} ${thread.thread_title || ''}`
    },
    toggleSql() {
      this.showSql = !this.showSql
    }
  }
}
</script>

<style scoped>
.v-card {
  border-radius: 8px !important;
}
.v-list-item:hover {
  background-color: #f5f5f5 !important;
  border-radius: 4px;
}
.v-text-field {
  border-radius: 4px !important;
}
.blue.lighten-5 {
  border-left: 4px solid blue;
}
.green.lighten-5 {
  border-left: 4px solid green;
}
.grey.lighten-4 {
  border-left: 4px solid grey;
}
.sql-block {
  background-color: #f4f4f4;
  border-radius: 4px;
  padding: 8px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  white-space: pre-wrap;
  overflow-x: auto;
}
.sql-block pre {
  margin: 0;
}
.sql-block code {
  color: #333;
}
.thinking-dots .dot {
  animation: blink 1.5s infinite;
  opacity: 0;
}
.thinking-dots .dot:nth-child(1) {
  animation-delay: 0s;
}
.thinking-dots .dot:nth-child(2) {
  animation-delay: 0.3s;
}
.thinking-dots .dot:nth-child(3) {
  animation-delay: 0.6s;
}
@keyframes blink {
  0% { opacity: 0; }
  50% { opacity: 1; }
  100% { opacity: 0; }
}
.sticky-input {
  position: sticky;
  bottom: 0;
  background: white;
  z-index: 1;
  border-top: 1px solid #e0e0e0;
}
</style>